import {
  BAR_STYLES,
  BAR_STYLE_LABELS,
  CANDLE_FINISHES,
  CANDLE_FINISH_LABELS,
  CANDLE_STYLES,
  CANDLE_STYLE_LABELS,
  CENTERPIECE_STYLES,
  CENTERPIECE_STYLE_LABELS,
  CHAIR_STYLES,
  CHAIR_STYLE_LABELS,
  CHUPPAH_STYLES,
  CHUPPAH_STYLE_LABELS,
  COCKTAIL_TABLE_STYLES,
  COCKTAIL_TABLE_STYLE_LABELS,
  CUTLERY_FINISHES,
  CUTLERY_FINISH_LABELS,
  DANCE_FLOOR_STYLES,
  DANCE_FLOOR_STYLE_LABELS,
  DECOR_FLOOR_FINISHES,
  DECOR_FLOOR_FINISH_LABELS,
  DECOR_LIBRARY_CATEGORY_SPECS,
  GLASS_SHAPES,
  GLASS_SHAPE_LABELS,
  GLASS_STYLES,
  GLASS_STYLE_LABELS,
  LINEN_FINISHES,
  LINEN_FINISH_LABELS,
  LOUNGE_STYLES,
  LOUNGE_STYLE_LABELS,
  NAPKIN_FOLDS,
  NAPKIN_FOLD_LABELS,
  type DecorField,
  type DecorLibraryCategory,
  type DecorLook,
} from '@peles/shared';

/**
 * How each field of the look is asked for on a screen — a row of choices, a
 * colour, a list of colours or yes/no. One table, so the Look panel, a
 * library piece and a single table's own look ask the same way.
 */
export type DecorFieldSpec =
  | { kind: 'choice'; label: string; options: ReadonlyArray<{ value: string; label: string }> }
  | { kind: 'color'; label: string }
  | { kind: 'colors'; label: string }
  | { kind: 'boolean'; label: string };

const choice = <T extends string>(
  label: string,
  list: readonly T[],
  labels: Record<T, string>,
): DecorFieldSpec => ({
  kind: 'choice',
  label,
  options: list.map((value) => ({ value, label: labels[value] })),
});

export const DECOR_FIELD_SPECS: Record<DecorField, DecorFieldSpec> = {
  chairStyle: choice('Chair style', CHAIR_STYLES, CHAIR_STYLE_LABELS),
  chairColor: { kind: 'color', label: 'Chair colour' },
  cushionColor: { kind: 'color', label: 'Cushion colour' },
  linenColor: { kind: 'color', label: 'Linen' },
  linenFinish: choice('Fabric', LINEN_FINISHES, LINEN_FINISH_LABELS),
  napkinColor: { kind: 'color', label: 'Napkins' },
  napkinFold: choice('Napkin fold', NAPKIN_FOLDS, NAPKIN_FOLD_LABELS),
  centerpiece: choice('On each table', CENTERPIECE_STYLES, CENTERPIECE_STYLE_LABELS),
  flowerColors: { kind: 'colors', label: 'Flower colours' },
  greeneryColor: { kind: 'color', label: 'Greenery' },
  candleStyle: choice('Candles', CANDLE_STYLES, CANDLE_STYLE_LABELS),
  candleFinish: choice('Candle holders', CANDLE_FINISHES, CANDLE_FINISH_LABELS),
  charger: { kind: 'boolean', label: 'Charger plates' },
  chargerColor: { kind: 'color', label: 'Charger colour' },
  plateColor: { kind: 'color', label: 'Plates' },
  cutlery: choice('Cutlery', CUTLERY_FINISHES, CUTLERY_FINISH_LABELS),
  glass: choice('Glasses', GLASS_STYLES, GLASS_STYLE_LABELS),
  glassColor: { kind: 'color', label: 'Glass colour' },
  glassShape: choice('Glass shape', GLASS_SHAPES, GLASS_SHAPE_LABELS),
  wallColor: { kind: 'color', label: 'Walls' },
  floorColor: { kind: 'color', label: 'Floor colour' },
  floorFinish: choice('Floor', DECOR_FLOOR_FINISHES, DECOR_FLOOR_FINISH_LABELS),
  cocktailStyle: choice('Cover', COCKTAIL_TABLE_STYLES, COCKTAIL_TABLE_STYLE_LABELS),
  cocktailColor: { kind: 'color', label: 'Cover colour' },
  barStyle: choice('Bar front', BAR_STYLES, BAR_STYLE_LABELS),
  barColor: { kind: 'color', label: 'Bar colour' },
  danceFloorStyle: choice('Dance floor', DANCE_FLOOR_STYLES, DANCE_FLOOR_STYLE_LABELS),
  chuppahStyle: choice('Chuppah', CHUPPAH_STYLES, CHUPPAH_STYLE_LABELS),
  chuppahFabricColor: { kind: 'color', label: 'Chuppah fabric' },
  chuppahFlowerColors: { kind: 'colors', label: 'Chuppah flowers' },
  loungeStyle: choice('Lounge style', LOUNGE_STYLES, LOUNGE_STYLE_LABELS),
  loungeColor: { kind: 'color', label: 'Upholstery' },
};

/** A field that only means something beside another value — hidden when it would not show. */
export function fieldApplies(field: DecorField, values: Record<string, unknown>): boolean {
  if (field === 'chargerColor') return values.charger !== false;
  if (field === 'glassColor') return values.glass === 'tinted';
  return true;
}

/** A field's value in words, for a card or a chip: "Chiavari", "#c9a35e", "Yes". */
export function describeFieldValue(field: DecorField, value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const spec = DECOR_FIELD_SPECS[field];
  if (spec.kind === 'choice') return spec.options.find((o) => o.value === value)?.label ?? null;
  if (spec.kind === 'boolean') return value ? 'Yes' : 'No';
  return null;
}

/** The words a piece's look reads as on its card: its styles, never its colours. */
export function lookSummary(category: DecorLibraryCategory, look: DecorLook): string[] {
  return DECOR_LIBRARY_CATEGORY_SPECS[category].fields
    .map((f) => {
      const text = describeFieldValue(f, (look as Record<string, unknown>)[f]);
      return text && DECOR_FIELD_SPECS[f].kind === 'boolean'
        ? `${DECOR_FIELD_SPECS[f].label}: ${text}`
        : text;
    })
    .filter((t): t is string => !!t);
}

/** The colours a look carries, for a card's swatches. */
export function lookColors(look: DecorLook): string[] {
  const out: string[] = [];
  Object.entries(look).forEach(([k, v]) => {
    if (k === 'picks') return;
    if (typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v)) out.push(v);
    if (Array.isArray(v)) v.forEach((c) => typeof c === 'string' && out.push(c));
  });
  return [...new Set(out)];
}
